import { generateUniquePrefix } from '../generateUniquePrefix'
import { getPageUrl } from './new-page/getPageUrl'
import { DATA_BINDING } from '@wix/app-definition-ids'
import {
  PAGE_TYPES,
  parseUrl,
  generateKey as generatePageLinkFieldKey,
} from '@wix/wix-data-client-common'
import type {
  AppApi,
  PagesToAdd,
  PageToAdd,
  PageType,
  ExtendedFieldSpec,
} from '@wix/wix-data-client-common'
import type { EditorSDK } from '@wix/wix-data-client-common-standalone'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import type { DpPageType } from '@wix/wix-data-client-common/src/appApiTypes'

export const determinePageLink = ({
  pageToAdd,
  routerPrefix,
  existingFieldKeys,
  collectionFields,
  displayField,
  isTPACollection,
  isEditPage,
}: {
  pageToAdd: PageToAdd
  routerPrefix: string
  existingFieldKeys: string[]
  collectionFields: ExtendedFieldSpec[]
  displayField?: string
  isTPACollection: boolean
  isEditPage: boolean
}) => {
  const pageType =
    pageToAdd.key === 'indexPage' ? PAGE_TYPES.COLLECTION : PAGE_TYPES.ITEM

  const url = getPageUrl({
    pageType,
    routerPrefix,
    collectionFields,
    displayField,
    isTPACollection,
    isEditPage,
  })

  const { pattern } = parseUrl(url)

  const pageLinkFieldKey = generatePageLinkFieldKey({
    prefix: routerPrefix,
    pattern,
    existingFieldKeys,
  })

  return { url, pattern, pageLinkFieldKey }
}

const determinePageLinks = ({
  pagesToAdd,
  collectionFields,
  routerPrefix,
  displayField,
  isTPACollection,
  isEditPage,
}: {
  pagesToAdd: PagesToAdd
  collectionFields: ExtendedFieldSpec[]
  routerPrefix: string
  displayField?: string
  isTPACollection: boolean
  isEditPage: boolean
}) => {
  const newPageLinks = {} as Record<
    DpPageType,
    ReturnType<typeof determinePageLink>
  >
  const existingFieldKeys = collectionFields.map(({ name }) => name)

  for (const pageToAdd of pagesToAdd) {
    const pageLink = determinePageLink({
      pageToAdd,
      routerPrefix,
      existingFieldKeys,
      collectionFields,
      displayField,
      isTPACollection,
      isEditPage,
    })

    newPageLinks[pageToAdd.key] = pageLink
    existingFieldKeys.push(pageLink.pageLinkFieldKey)
  }

  return newPageLinks
}

const determineManagingAppDefId = async ({
  pagesToAdd,
  editorSdkProxy,
}: {
  pagesToAdd: PagesToAdd
  editorSdkProxy: EditorSDK
}) => {
  const staticPageRef = pagesToAdd.find(p => p.staticPageRef)?.staticPageRef

  if (staticPageRef) {
    const pageData = await editorSdkProxy.pages.getPageData({
      pageRef: staticPageRef,
    })

    if (pageData.managingAppDefId) {
      return pageData.managingAppDefId
    }
  }

  return DATA_BINDING
}

const clearPageUriSEO = ({
  editorSdkProxy,
  pageRef,
}: {
  editorSdkProxy: EditorSDK
  pageRef: PageRef
}) =>
  editorSdkProxy.document.pages.data.update({
    pageRef,
    data: { pageUriSEO: '' },
  })

export const createPrefixWithSafelyClearedStaticPageUriSEO = async ({
  editorSdkProxy,
  pagesToAdd,
  createPrefix,
}: {
  editorSdkProxy: EditorSDK
  pagesToAdd: PagesToAdd
  createPrefix: () => Promise<string>
}) => {
  const pageRef = pagesToAdd.find(p => p.staticPageRef)?.staticPageRef
  if (!pageRef) {
    return createPrefix()
  }

  await clearPageUriSEO({ editorSdkProxy, pageRef })
  return createPrefix()
}

export const prepareDataForPages = async ({
  appApi,
  editorSdkProxy,
  collectionId,
  collectionName,
  pagesToAdd,
  collectionFields,
  displayField,
  isTPACollection,
  pageType,
  isEditPage,
}: {
  appApi: AppApi
  editorSdkProxy: EditorSDK
  collectionId: string
  collectionName: string
  pagesToAdd: PagesToAdd
  collectionFields: ExtendedFieldSpec[]
  displayField?: string
  isTPACollection: boolean
  pageType: PageType
  isEditPage: boolean
}) => {
  const [routerPrefix, managingAppDefId] = await Promise.all([
    createPrefixWithSafelyClearedStaticPageUriSEO({
      editorSdkProxy,
      pagesToAdd,
      createPrefix: () =>
        generateUniquePrefix({
          inputsForPrefix: [collectionName, collectionId],
          editorSdkProxy,
          appApi,
          pageType,
        }),
    }),
    determineManagingAppDefId({
      pagesToAdd,
      editorSdkProxy,
    }),
  ])

  const pageLinks = determinePageLinks({
    pagesToAdd,
    collectionFields,
    routerPrefix,
    displayField,
    isTPACollection,
    isEditPage,
  })

  return {
    routerPrefix,
    managingAppDefId,
    pageLinks,
  }
}
